import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, FormControl, Grid, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { ageing, formatNumber, transformChartDataToTable } from "../../../../helpers/UtilityHelper";
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import _ from "lodash";
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import ChartTooltip from "../../../charts/ChartTooltip";
import ChartEmpty from "../../../charts/ChartEmpty";
import { ATTEMPT_COLORS as ATTEMPT_PALETTE } from "../../../charts/colors";
import InfoBox from "../../../../common/InfoBox";
import MaterialTable from "../../../../common/MaterialTable";

const ATTEMPT_COLORS = {
    '0_Attempts': ATTEMPT_PALETTE[0],
    '1_Attempts': ATTEMPT_PALETTE[1],
    '2_Attempts': ATTEMPT_PALETTE[2],
    '3_Attempts': ATTEMPT_PALETTE[3],
    '3+_Attempts': ATTEMPT_PALETTE[4],
}

const ATTEMPT_LABELS = {
    '0_Attempts': 'RTO / 0 Delivery Attempts',
    '1_Attempts': 'RTO / 1 Delivery Attempt',
    '2_Attempts': 'RTO / 2 Delivery Attempts',
    '3_Attempts': 'RTO / 3 Delivery Attempts',
    '3+_Attempts': 'RTO / 3+ Delivery Attempts',
}

export default function RTOAnalysis({ data }) {
    const [view, setView] = React.useState("chart")
    const { rows, allColumns } = transformChartDataToTable(data, [{ key: 'totalRtoCases', 'label': 'Total RTO' }, ...ageing.map(r => ({ key: r, label: r.replace("_", " ") }))])
    const [attemptData, setAttemptData] = useState();

    useEffect(() => {
        setAttemptData(data);
    }, [data]);

    const chartData = (attemptData || []).map((row) => {
        const total = ageing.reduce((s, z) => s + (row[z] || 0), 0)
        return { ...row, _stackTotal: total }
    })
    const rtoChartMax = chartData.reduce((m, r) => Math.max(m, r._stackTotal || 0), 0) || 1
    const rtoPlotHeight = 340

    const sums = {
        total_0_attempts: _.sumBy(data, '0_Attempts'),
        total_1_attempts: _.sumBy(data, '1_Attempts'),
        total_2_attempts: _.sumBy(data, '2_Attempts'),
        total_3_attempts: _.sumBy(data, '3_Attempts'),
        total_3_plus_attempts: _.sumBy(data, '3+_Attempts'),
    };

    const tableColumns = [
        {
            label: 'Name',
            value: 'name',
            align: 'left'
        },
        ...(allColumns.map((z) => ({
            numeric: true,
            align: 'right',
            value: (row) => {
                return formatNumber(row[z])
            },
            label: z
        })))
    ]

    const [att, setAtt] = useState(5)

    const handleChange = (event) => {
        setAtt(event.target.value);
        let updatedArray = []
        if (event.target.value === 0) {
            const keysToRemove = ["1_Attempts", "2_Attempts", "3_Attempts", "3+_Attempts"];
            updatedArray = data.map(obj => _.omit(obj, keysToRemove));
        }
        if (event.target.value === 1) {
            const keysToRemove = ["0_Attempts", "2_Attempts", "3_Attempts", "3+_Attempts"];
            updatedArray = data.map(obj => _.omit(obj, keysToRemove));
        }
        if (event.target.value === 2) {
            const keysToRemove = ["0_Attempts", "1_Attempts", "3_Attempts", "3+_Attempts"];
            updatedArray = data.map(obj => _.omit(obj, keysToRemove));
        }
        if (event.target.value === 3) {
            const keysToRemove = ["0_Attempts", "1_Attempts", "2_Attempts", "3+_Attempts"];
            updatedArray = data.map(obj => _.omit(obj, keysToRemove));
        }
        if (event.target.value === 4) {
            const keysToRemove = ["0_Attempts", "1_Attempts", "2_Attempts", "3_Attempts"];
            updatedArray = data.map(obj => _.omit(obj, keysToRemove));
        }
        if (event.target.value === 5) {
            updatedArray = data;
        }
        setAttemptData(updatedArray)
    };

    return (
        <Card elevation={1}>
            <CardHeader title="RTO - Delivery Reattempt Analysis" subheader={
                <Grid container spacing={1} sx={{ mt: 2 }}>
                    <Grid item xs={2.4}>
                        <InfoBox header="RTO / 0 Delivery Attempts" value={sums.total_0_attempts} />
                    </Grid>
                    <Grid item xs={2.4}>
                        <InfoBox header="RTO / 1 Delivery Attempts" value={sums.total_1_attempts} />
                    </Grid>
                    <Grid item xs={2.4}>
                        <InfoBox header="RTO / 2 Delivery Attempts" value={sums.total_2_attempts} />
                    </Grid>
                    <Grid item xs={2.4}>
                        <InfoBox header="RTO / 3 Delivery Attempts" value={sums.total_3_attempts} />
                    </Grid>
                    <Grid item xs={2.4}>
                        <InfoBox header="RTO / 3+ Delivery Attempts" value={sums.total_3_plus_attempts} />
                    </Grid>
                </Grid>
            } action={(
                <ToggleButtonGroup color="primary" size="small" exclusive onChange={(e, v) => setView(v)} value={view}>
                    <ToggleButton value="chart">
                        <BarChartIcon />
                    </ToggleButton>
                    <ToggleButton value="table">
                        <TableChartIcon />
                    </ToggleButton>
                </ToggleButtonGroup>
            )} />
            <CardContent>
                {view === 'chart' && (
                    <React.Fragment>
                        <FormControl size="small" sx={{ minWidth: 150, mb: 1 }}>
                            <InputLabel id="rto-attempt-label">Attempt</InputLabel>
                            <Select
                                labelId="rto-attempt-label"
                                value={att}
                                label="Attempt"
                                onChange={handleChange}
                                sx={{ fontSize: '0.8rem' }}
                            >
                                <MenuItem value={5} sx={{ fontSize: '0.8rem' }}>All</MenuItem>
                                <MenuItem value={0} sx={{ fontSize: '0.8rem' }}>0 Attempts</MenuItem>
                                <MenuItem value={1} sx={{ fontSize: '0.8rem' }}>1 Attempt</MenuItem>
                                <MenuItem value={2} sx={{ fontSize: '0.8rem' }}>2 Attempts</MenuItem>
                                <MenuItem value={3} sx={{ fontSize: '0.8rem' }}>3 Attempts</MenuItem>
                                <MenuItem value={4} sx={{ fontSize: '0.8rem' }}>3+ Attempts</MenuItem>
                            </Select>
                        </FormControl>
                        {(!chartData || chartData.length === 0) ? <ChartEmpty height={420} /> : (
                        <ResponsiveContainer width="100%" height={420}>
                            <ComposedChart data={chartData} margin={{ top: 30, right: 20, bottom: 5, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
                                <XAxis dataKey="name" fontSize="11px" fontWeight="500" />
                                <YAxis fontSize="11px" fontWeight="500" label={{ value: '# RTO', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
                                <Tooltip content={<ChartTooltip />} />
                                <Legend verticalAlign="top" align="left" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500, paddingBottom: 10 }} />
                                {ageing.map((z, i) => {
                                    const color = ATTEMPT_COLORS[z] || '#999'
                                    const isLast = i === ageing.length - 1
                                    return (
                                        <Area
                                            key={z}
                                            dataKey={z}
                                            name={ATTEMPT_LABELS[z] || z.replace('_', ' ')}
                                            stackId="attempts"
                                            type="monotone"
                                            stroke={color}
                                            fill={color}
                                            fillOpacity={0.85}
                                            strokeWidth={1}
                                            animationEasing="ease-in-out"
                                            animationDuration={1000}
                                        >
                                            <LabelList
                                                dataKey={z}
                                                content={({ x, y, value }) => {
                                                    if (!value || value === 0 || x == null || y == null) return null
                                                    const segPx = (value / rtoChartMax) * rtoPlotHeight
                                                    if (segPx < 16) return null
                                                    return (
                                                        <text
                                                            x={x}
                                                            y={y + segPx / 2 + 3}
                                                            fill="rgba(0,0,0,0.55)"
                                                            fontSize={9}
                                                            fontWeight={500}
                                                            textAnchor="middle"
                                                        >
                                                            {value}
                                                        </text>
                                                    )
                                                }}
                                            />
                                            {isLast && (
                                                <LabelList dataKey="_stackTotal" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#374151' }} formatter={(v) => (v == null || v === 0) ? '' : v} />
                                            )}
                                        </Area>
                                    )
                                })}
                            </ComposedChart>
                        </ResponsiveContainer>
                        )}
                    </React.Fragment>
                )}
                {view === 'table' && (
                    <MaterialTable size="small" exportExcel={false} rows={rows} columns={tableColumns} frozenColumnsCount={1} />
                )}
            </CardContent>
        </Card>
    )
}